import { PointsTooltip, VoteModifiedTooltip, VoteTooltip } from '../results-page/score-section';
import React, { useCallback, useEffect, useState } from 'react';
import DOMPurify from 'dompurify';

import {
    calculateVoteTraining,
    correctAllExercises,
    getCorrectionByStatusId,
    getCorrectionBySubmissionId,
    getGoogleStudentInfo,
    pauseHomework as pauseHomeworkAction,
    swapCorrectionMode,
    terminateHomework,
} from '../../actions/status';
import { useGapi, useHeartbeat, useMathJaxStatus } from '../../lib/hooks';
import { getSectionCompletionTime } from '../../lib/status';
import { getSourceLabel, getTimeString, setPageTitle } from '../../lib/utils';

import { Body } from '../../components/typography';
import Button from '../../components/button';
import { CORRECTION } from '../../constants/execution-modes';
import ColumnContainer from '../../components/layouts/components/column-container';
import CorrectionBubbles from '../correction-bubbles';
import CorrectionLoadError from '../../components/correction-load-error';
import CorrectionResults from '../../components/correction-results';
import CorrectionResultsBySections from '../../components/correction-results-by-sections';
import DialogTrigger from '../../components/dialog-trigger';
import FluidContainer from '../../components/layouts/components/fluid-container';
import GeneralTooltip from '../../components/general-tooltip';
import Hidden from '../../components/layouts/components/hidden';
import ModalContainer from '../modal';
import PropTypes from 'prop-types';
import QuestionIcon from './assets/questionMarkCircle.svg?react';
import { SOURCES } from '../../constants/sources';
import { Show } from '../../components/show';
import Skeleton from '../../components/skeleton';
import Stack from '../../components/stack';
import Switch from '../../components/switch';
import { connect } from 'react-redux';
import { getAssignmentById, getGoogleAssignmentEndDate } from '../../actions/assignment';
import { initializeApplication } from '../../actions/application';
import moment from 'moment';
import { onPlayVideo } from '../../actions/media-player';
import { parseHomeworkId } from '../../lib/homework';
import { resumeLogin } from '../../actions/auth';
import { setHomework } from '../../actions/homework';
import { showNotLoggedModal as showNotLoggedModalAction } from '../../actions/modal';
import styled from 'styled-components';
import { FUNCTIONARY_GROUP_ID, SCHOOL_TEACHER_GROUP_ID, UNIVERSITY_TEACHER_GROUP_ID } from '../../config';
import { resumeGoogleLogin, resumeGoogleAddOnLogin } from '../../actions/google-auth';

const ButtonsContainer = styled.div`
    display: flex;
`;

export const CorrectionTitle = styled.div`
    font-size: 24px;
    font-weight: 600;
    letter-spacing: 0;
    margin-bottom: 16px;
`;

const Message = styled.span`
    font-size: 16px;
    letter-spacing: 0;
    line-height: 24px;
    margin-top: 0;
    padding-bottom: ${(props) => props.theme.space.base}px;
`;

const AlertMessage = styled(Message)`
    color: var(--red800);
`;

export const Title = styled.div`
    font-size: 18px;
    font-weight: 600;
    line-height: 24px;
    text-overflow: ellipsis;
    -webkit-line-clamp: 2;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
`;

export const User = styled.div`
    font-size: 18px;
    font-weight: 600;
    line-height: 24px;
`;

export const Info = styled.span`
    display: flex;
    align-items: center;
    font-size: 16px;
    line-height: 24px;

    & > * {
        margin-right: 4px;
    }

    & svg {
        width: 18px;
        height: 18px;
        fill: ${(props) => props.theme.colors['myzanichelli-blue-main']};
    }
`;

const Header = styled(FluidContainer)`
    flex-direction: column-reverse;

    ${(props) => props.theme.mediaQueries.desktop} {
        flex-direction: row;
    }
`;

const LinkTootltip = styled.a`
    color: white;
    text-decoration: underline;
`;

export const DEFAULT_CALLBACK_URL = `https://${window.location.hostname}/correzioni`;

export const parseDate = (dateTime) => {
    const parsedDate = moment(dateTime);

    return parsedDate.isValid() ? parsedDate.format('DD/MM/YYYY HH:mm') : '';
};

export const parseSubject = (subjects) => {
    const formattedSubjects = subjects.reduce((str, subject) => {
        return `${str}${subject.name}, `;
    }, '');
    return formattedSubjects.substring(0, formattedSubjects.length - 2);
};

const scoreUnitutor = (exercises, scoreModifier, score) => {
    if (exercises.length === 0) {
        return '-/-';
    }

    const maxScore = exercises.length * scoreModifier.correct;
    return `${score.toFixed(2)}/${maxScore}`;
};

const calculateMaxScore = (exercises) => {
    return exercises.reduce((accumulator, exercise) => {
        return isNaN(exercise.difficulty) ? accumulator : accumulator + exercise.difficulty;
    }, 0);
};

const calculateScore = (exercises, correctionPoints) => {
    return correctionPoints.reduce((accumulator, correction) => {
        const exercise = exercises.find((e) => e.id === correction.exerciseId);
        const difficulty = exercise && exercise.difficulty ? exercise.difficulty : 0;
        const value = correction.totalAnswers === 0 ? 0 : correction.correctAnswers / correction.totalAnswers;
        return accumulator + value * difficulty;
    }, 0);
};

const scoreSchoolUniversity = (exercises, correctionPoints) => {
    if (exercises.length === 0 || correctionPoints === 0) {
        return '-/-';
    }

    const maxScore = calculateMaxScore(exercises);
    const score = calculateScore(exercises, correctionPoints);

    return `${score.toFixed(2)}/${maxScore}`;
};

const voteSchoolUniversity = (correctionPoints, exercises) => {
    if (!correctionPoints || !exercises) {
        return '-';
    }

    const totalScore = calculateMaxScore(exercises);
    if (totalScore === 0) {
        return '-';
    }

    const score = calculateScore(exercises, correctionPoints);

    const vote = (score / totalScore) * 10;
    return vote.toFixed(1);
};

const CorrectionPage = ({
    answers,
    assignment,
    assignmentId,
    backSource,
    bookId,
    calculateVoteRequest,
    calculateVoteTraining,
    callbackLabel,
    callbackUrl,
    correctAllExercises,
    correctionPoints,
    correctionsStatus,
    courseTitle,
    email,
    endDate,
    error,
    executionMode,
    exercises,
    firstName,
    geogebraExercises,
    getAssignmentById,
    getGoogleAssignmentEndDate,
    getCorrectionByStatusId,
    getCorrectionBySubmissionId,
    getGoogleStudentInfo,
    googleCourseWorkId,
    googleUserInfo,
    googleUserStatus,
    heartbeatPulse,
    homeworkOriginalId,
    homeworkVersion,
    httpStatusCodeAuth,
    httpStatusCodeStatus,
    idCat,
    initializeApplication,
    isAuthLoaded,
    isAuthLoading,
    isLtiLaunch,
    isOldCorrection,
    isReady,
    userAllowed,
    lastName,
    modificationTime,
    notLoggedModalShowed,
    onPlayVideo,
    pauseHomework,
    publicTitle,
    remainingTime,
    resumeLogin,
    resumeGoogleLogin,
    resumeGoogleAddOnLogin,
    saveStatus,
    score,
    originalScore,
    scoreModifier,
    setHomework,
    showNotLoggedModal,
    singleAnswer,
    source,
    status,
    subjects,
    swapCorrectionMode,
    terminateHomework,
    time = 0,
    videos,
}) => {
    const [gApiStatus] = useGapi();
    const [applicationPayload, setApplicationPayload] = useState(null);
    const [correctionType, setCorrectionType] = useState('ALL');
    const [expand, setExpand] = useState(false);
    const [showNewCorrection, setShowNewCorrection] = useState(false);
    const [hasPartialPoints, setHasPartialPoints] = useState(false);
    const isMathJaxLoading = useMathJaxStatus();
    const googleAddon = applicationPayload?.profile.googleAddon;
    useHeartbeat();

    useEffect(() => {
        if (!notLoggedModalShowed && !heartbeatPulse) {
            status?.id && pauseHomework(status, saveStatus);
            showNotLoggedModal();
        }
    }, [heartbeatPulse, notLoggedModalShowed, pauseHomework, saveStatus, showNotLoggedModal, status]);

    useEffect(() => {
        setPageTitle(source, publicTitle);
    }, [source, publicTitle]);

    useEffect(() => {
        setHasPartialPoints(correctionPoints.some((c) => !!c.totalAnswers));
    }, [correctionPoints]);

    useEffect(() => {
        if (isOldCorrection && calculateVoteRequest.error) {
            setShowNewCorrection(false);
        }
    }, [isOldCorrection, calculateVoteRequest.error, setShowNewCorrection]);

    useEffect(() => {
        if (isReady || applicationPayload !== null) {
            return;
        }

        const initData = async () => {
            const result = await initializeApplication(CORRECTION);
            result && setApplicationPayload(result);
        };

        initData();
    }, [applicationPayload, initializeApplication, isReady]);

    useEffect(() => {
        if (googleCourseWorkId && gApiStatus.success) {
            resumeGoogleLogin();
        }
    }, [gApiStatus.success, googleCourseWorkId, resumeGoogleLogin]);

    useEffect(() => {
        if (googleAddon) {
            resumeGoogleAddOnLogin(applicationPayload?.profile.googleIdToken, applicationPayload?.profile.googleAccessToken);
        }
    }, [googleAddon, applicationPayload, resumeGoogleAddOnLogin]);

    useEffect(() => {
        if (applicationPayload === null || isAuthLoading || isAuthLoaded) {
            return;
        }
        const setUserInfo = async () => {
            if (!googleCourseWorkId && !googleAddon) {
                await resumeLogin(source === SOURCES.LAZE && isLtiLaunch);
            }
        };

        setUserInfo();
    }, [applicationPayload, googleCourseWorkId, isAuthLoaded, isAuthLoading, resumeLogin, googleAddon, isLtiLaunch]);

    const initData = useCallback(
        async (profile) => {
            let r = null;
            if (profile.googleAddon) {
                r = await getCorrectionBySubmissionId(profile.googleSubmissionId);
            } else {
                r = await getCorrectionByStatusId(profile.statusId);
            }
            const { homework, status } = r;

            if (status.profileValues && status.profileValues.shuffle !== null) {
                profile.shuffle = status.profileValues.shuffle;
            }

            const data = await setHomework(homework, status, profile);
            const homeworkExercises = data.exercises;
            const homeworkAnswers = status.answers || [];
            await terminateHomework(homeworkExercises, homeworkAnswers, status, false);
            correctAllExercises(homeworkExercises, homeworkAnswers, true);
            if (status.assignmentId) {
                let assignment = await getAssignmentById(status.assignmentId);

                if (profile.googleCourseWorkId) {
                    const googleAssignmentEndDate = await getGoogleAssignmentEndDate(assignment.classId, assignment.googleCourseWorkId);

                    getGoogleStudentInfo(assignment.classId, status.studentId);

                    assignment.endDate = googleAssignmentEndDate;
                }
            }
        },
        [
            correctAllExercises,
            getAssignmentById,
            getCorrectionByStatusId,
            getCorrectionBySubmissionId,
            getGoogleAssignmentEndDate,
            getGoogleStudentInfo,
            setHomework,
            terminateHomework,
        ],
    );

    useEffect(() => {
        if (isAuthLoaded && applicationPayload?.profile) {
            initData(applicationPayload?.profile).catch(() => {});
        }
    }, [applicationPayload, initData, isAuthLoaded]);

    const book = courseTitle ? ` | ${courseTitle}` : '';
    const parsedSubjects = subjects && subjects.length > 0 ? ` | ${parseSubject(subjects)}` : '';

    const getCallbackUrl = () => {
        if (!callbackUrl) {
            return !!document.referrer ? document.referrer : DEFAULT_CALLBACK_URL;
        }

        return callbackUrl;
    };

    const currentDate = new Date().getTime();
    const assignmentEndDate = Date.parse(endDate);
    const showCorrection =
        userAllowed ||
        (assignmentId ? currentDate >= assignmentEndDate || singleAnswer : true) ||
        assignment.teacherId === googleUserInfo?.id;

    const homeworkId = parseHomeworkId(homeworkOriginalId, idCat, source);

    const applicationName = getSourceLabel(backSource);
    if (executionMode !== CORRECTION && (isReady || error)) {
        return (
            <CorrectionLoadError title="Oh no, qualcosa è andato storto!" source={backSource}>
                {!isLtiLaunch && !googleAddon && (
                    <>
                        <br />
                        {`Torna su ${applicationName} e cerca di nuovo la prova.`}
                    </>
                )}
                <br />
                {'Hai bisogno di aiuto? Contattaci'}
            </CorrectionLoadError>
        );
    }

    if (error && httpStatusCodeAuth === 401) {
        const messageLabel = isLtiLaunch ? `sei autenticato` : `entri in ${applicationName}`;
        const applicationLabel = isLtiLaunch ? `Inserisci` : `Torna su ${applicationName}, inserisci`;
        return (
            <CorrectionLoadError title={`Puoi vedere questa correzione solo se ${messageLabel}`} source={backSource}>
                {`${applicationLabel} le tue credenziali e riapri la correzione.`}
                <br />
                {'Non riesci a vederla? Contattaci.'}
            </CorrectionLoadError>
        );
    }

    if (error && googleAddon && httpStatusCodeStatus === 404) {
        return (
            <CorrectionLoadError title="Lo Studente non ha ancora svolto la prova." source={null}>
                {'Riprovare ad aprire la correzione dopo che lo Studente avrà terminato lo svolgimento.'}
                <br />
                {'Hai bisogno di aiuto? Contattaci'}
            </CorrectionLoadError>
        );
    }

    if (error && httpStatusCodeStatus !== 403) {
        return (
            <CorrectionLoadError title="Oh no, qualcosa è andato storto!" source={backSource}>
                {'Non siamo riusciti a caricare la correzione.'}
                {!isLtiLaunch && !googleAddon && (
                    <>
                        <br />
                        <br />
                        {`Torna su ${applicationName} e seleziona di nuovo la prova da controllare.`}
                    </>
                )}
                <br />
                {'Hai bisogno di aiuto? Contattaci'}
            </CorrectionLoadError>
        );
    }

    if (error && httpStatusCodeStatus === 403) {
        return (
            <CorrectionLoadError title="La correzione di questa prova è riservata" source={backSource}>
                {"La correzione di una prova può essere mostrata solo a chi l'ha svolta o a chi l'ha assegnata."}
                {!isLtiLaunch && !googleAddon && (
                    <>
                        <br />
                        <br />

                        {`Torna su ${applicationName}, accedi e prova ad aprire di nuovo la correzione.`}
                    </>
                )}
                <br />
                {'Hai bisogno di aiuto? Contattaci'}
            </CorrectionLoadError>
        );
    }

    const onCalculateVoteSwitchChange = (checked) => {
        if (calculateVoteRequest.loading) {
            return;
        }

        if (!checked) {
            swapCorrectionMode();
            setShowNewCorrection(false);
            return;
        }

        setShowNewCorrection(true);
        if (calculateVoteRequest.cache.tempPoints) {
            swapCorrectionMode();
            return;
        }

        calculateVoteTraining(source, homeworkOriginalId, bookId, homeworkVersion, answers, scoreModifier);
    };

    const isModified = originalScore !== score;

    let userData = firstName;
    if (lastName) {
        userData += ` ${lastName}`;
    }
    if (email) {
        userData += ` (${email})`;
    }

    const completionTime = !!status.sections
        ? status.sections.reduce((partialTime, section) => partialTime + getSectionCompletionTime(section), 0)
        : time - remainingTime;

    return (
        <FluidContainer paddingBottom={80} flexDirection="column" alignItems="center">
            <ModalContainer />
            <Show when={!isLtiLaunch && callbackLabel && !googleAddon}>
                <Hidden above="tablet">
                    <ColumnContainer width={1}>
                        <FluidContainer padding={['mobile', 'tablet', 'desktop']}>
                            <Button type="secondary" fluid={true} as="a" href={DOMPurify.sanitize(getCallbackUrl())}>
                                {`Torna su ${callbackLabel}`}
                            </Button>
                        </FluidContainer>
                    </ColumnContainer>
                </Hidden>
            </Show>

            <ColumnContainer width={[0, 0, 1 / 12]} />
            <ColumnContainer width={[1, 1, 10 / 12]}>
                <Header>
                    <Hidden print={true}>
                        <ColumnContainer width={[1, 1, 4 / 12]}>
                            <FluidContainer padding={['mobile', 'tablet', 'desktop']} flexDirection="column">
                                <Skeleton
                                    isLoading={!isReady}
                                    items={[
                                        { height: 'large', width: 20 },
                                        { height: 'large', width: 20 },
                                        { height: 'large', width: 20 },
                                    ]}
                                    direction="horizontal"
                                    gap="regular"
                                >
                                    <Show when={showCorrection}>
                                        <CorrectionBubbles
                                            onClick={(value) =>
                                                correctionType === value ? setCorrectionType('ALL') : setCorrectionType(value)
                                            }
                                            selectedBubble={correctionType}
                                        />
                                    </Show>
                                </Skeleton>
                            </FluidContainer>
                        </ColumnContainer>
                    </Hidden>

                    <ColumnContainer width={[1, 1, 6 / 12]}>
                        <ColumnContainer width={[1, 1 / 2, 3 / 12]}>
                            <FluidContainer padding={['mobile', 'tablet', 'desktop']} flexDirection="column">
                                <Skeleton isLoading={!isReady} items={[{ width: 30 }, { width: 100 }, { width: 60 }]}>
                                    <Title dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(publicTitle) }} />
                                    <Info
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(`Prova (ID): #${homeworkId}${book}${parsedSubjects}`),
                                        }}
                                    />
                                </Skeleton>
                            </FluidContainer>
                        </ColumnContainer>
                        <ColumnContainer width={[1, 1 / 2, 3 / 12]}>
                            <FluidContainer padding={['mobile', 'tablet', 'desktop']} flexDirection="column">
                                <Skeleton isLoading={!isReady} items={[{ width: 60 }, { width: 100 }, { width: 30 }]}>
                                    {!googleAddon && <User>{userData}</User>}
                                    <Info>
                                        <strong>{'Data svolgimento: '}</strong>
                                        {parseDate(modificationTime)}
                                    </Info>

                                    <Show when={completionTime >= 0 && !!time}>
                                        <Info>
                                            <strong>{'Tempo totale impiegato: '}</strong>
                                            {getTimeString(completionTime)}
                                        </Info>
                                    </Show>

                                    {(source === SOURCES.ZTE_SCUOLA || source === SOURCES.ZTE_UNIVERSITA || source === SOURCES.LAZE) &&
                                        hasPartialPoints && (
                                            <Info>
                                                <strong>{'Punteggio: '}</strong>
                                                <span>{scoreSchoolUniversity(exercises, correctionPoints)}</span>
                                                <DialogTrigger>
                                                    <QuestionIcon />
                                                    <GeneralTooltip position="bottom">
                                                        <PointsTooltip />
                                                    </GeneralTooltip>
                                                </DialogTrigger>
                                            </Info>
                                        )}

                                    {source === SOURCES.ZTE_UNITUTOR && (
                                        <Info>
                                            <strong>{'Punteggio: '}</strong>
                                            <span>
                                                {scoreUnitutor(exercises, scoreModifier, score)}
                                                {isModified && '*'}
                                            </span>
                                            <DialogTrigger>
                                                <QuestionIcon />
                                                <GeneralTooltip position="bottom">
                                                    {isModified ? (
                                                        <VoteModifiedTooltip
                                                            assignment={assignment}
                                                            userAllowed={userAllowed}
                                                            originalScore={originalScore}
                                                            source={source}
                                                        />
                                                    ) : (
                                                        <PointsTooltip />
                                                    )}
                                                </GeneralTooltip>
                                            </DialogTrigger>
                                        </Info>
                                    )}

                                    {(source === SOURCES.ZTE_SCUOLA || source === SOURCES.ZTE_UNIVERSITA || source === SOURCES.LAZE) && (
                                        <Info>
                                            <strong>{'Voto in decimi: '}</strong>
                                            <span>
                                                {score !== null && !isNaN(score)
                                                    ? `${score.toFixed(1)}`
                                                    : `${voteSchoolUniversity(correctionPoints, exercises)}`}
                                                {isModified && '*'}
                                            </span>
                                            {hasPartialPoints && (
                                                <DialogTrigger>
                                                    <QuestionIcon />
                                                    <GeneralTooltip position="bottom">
                                                        {isModified ? (
                                                            <VoteModifiedTooltip
                                                                assignment={assignment}
                                                                userAllowed={userAllowed}
                                                                originalScore={originalScore}
                                                                source={source}
                                                            />
                                                        ) : (
                                                            <VoteTooltip />
                                                        )}
                                                    </GeneralTooltip>
                                                </DialogTrigger>
                                            )}
                                        </Info>
                                    )}

                                    {(source === SOURCES.ZTE_SCUOLA || source === SOURCES.ZTE_UNIVERSITA || source === SOURCES.LAZE) &&
                                        isOldCorrection && (
                                            <Stack gap="xs">
                                                <Info>
                                                    <Switch
                                                        label="Vedi il nuovo voto"
                                                        onToggle={onCalculateVoteSwitchChange}
                                                        value={showNewCorrection}
                                                    />
                                                    {(!showNewCorrection || !calculateVoteRequest.loaded) && (
                                                        <DialogTrigger>
                                                            <QuestionIcon />
                                                            <GeneralTooltip position="bottom">
                                                                <FluidContainer width={256} flexDirection="column">
                                                                    <span>
                                                                        {
                                                                            'Guarda il nuovo voto e il punteggio della prova da cui lo abbiamo ricalcolato. Se chiudi la pagina scompare, ma potrai rivederlo ancora. '
                                                                        }
                                                                        <LinkTootltip href="/support">
                                                                            {'Leggi le domande frequenti per capire come funziona.'}
                                                                        </LinkTootltip>
                                                                    </span>
                                                                </FluidContainer>
                                                            </GeneralTooltip>
                                                        </DialogTrigger>
                                                    )}
                                                </Info>
                                                {calculateVoteRequest.error && (
                                                    <Body lv={5} weight="semi-bold" color="text-zanichelli-red-main">
                                                        {"Oh no, c'è stato un problema. Riprova."}
                                                    </Body>
                                                )}
                                            </Stack>
                                        )}
                                </Skeleton>
                            </FluidContainer>
                        </ColumnContainer>
                    </ColumnContainer>
                </Header>

                <FluidContainer flexDirection="column" paddingX={['mobile', 'tablet', 'desktop']}>
                    <Hidden print={true}>
                        <FluidContainer alignItems="center" justifyContent="space-between">
                            <CorrectionTitle>{'Correzione'}</CorrectionTitle>
                            <ButtonsContainer>
                                <Hidden below="tablet">
                                    <Show when={!isLtiLaunch && callbackLabel && !googleAddon}>
                                        <Button type="tertiary" size="small" as="a" href={DOMPurify.sanitize(getCallbackUrl())}>
                                            {`Torna su ${callbackLabel}`}
                                        </Button>
                                    </Show>
                                    <Button
                                        type="tertiary"
                                        onClick={() => {
                                            window.print();
                                        }}
                                        size="small"
                                        disabled={!isReady || isMathJaxLoading}
                                    >
                                        {'Stampa'}
                                    </Button>
                                </Hidden>

                                <Button type="tertiary" onClick={() => setExpand(!expand)} size="small" disabled={!isReady}>
                                    {expand ? 'NASCONDI TUTTO' : 'VEDI TUTTO'}
                                </Button>
                            </ButtonsContainer>
                        </FluidContainer>
                    </Hidden>

                    <Skeleton
                        isLoading={!isReady}
                        items={[
                            { height: 'large', width: 80 },
                            { height: 'large', width: 100 },
                            { height: 'large', width: 100 },
                            { height: 'large', width: 60 },
                        ]}
                        gap="base"
                    >
                        <Show when={!showCorrection && !googleUserStatus.pending}>
                            <AlertMessage>
                                {'Attenzione: potrai vedere voto e correzione delle verifiche assegnate solo dopo la data di consegna.'}
                            </AlertMessage>
                        </Show>
                        <Show when={showCorrection}>
                            {!!status.sections ? (
                                <CorrectionResultsBySections
                                    answers={answers}
                                    exercises={exercises}
                                    filter={correctionType}
                                    expand={expand}
                                    correctionsStatus={correctionsStatus}
                                    videos={videos}
                                    onPlayVideo={onPlayVideo}
                                    geogebraExercises={geogebraExercises}
                                    correctionPoints={correctionPoints}
                                    source={source}
                                    sections={status.sections}
                                    scoreModifier={status.scoreModifier}
                                    showScore={true}
                                />
                            ) : (
                                <CorrectionResults
                                    answers={answers}
                                    correctionsStatus={correctionsStatus}
                                    exercises={exercises}
                                    expand={expand}
                                    filter={correctionType}
                                    geogebraExercises={geogebraExercises}
                                    onPlayVideo={onPlayVideo}
                                    videos={videos}
                                    correctionPoints={correctionPoints}
                                    source={source}
                                />
                            )}
                        </Show>
                    </Skeleton>
                </FluidContainer>
            </ColumnContainer>
        </FluidContainer>
    );
};

CorrectionPage.propTypes = {
    answers: PropTypes.array,
    assignment: PropTypes.object,
    assignmentId: PropTypes.string,
    backSource: PropTypes.string,
    bookId: PropTypes.number,
    calculateVoteRequest: PropTypes.object,
    calculateVoteTraining: PropTypes.func,
    callbackLabel: PropTypes.string,
    callbackUrl: PropTypes.string,
    correctAllExercises: PropTypes.func,
    correctionPoints: PropTypes.array,
    correctionsStatus: PropTypes.array,
    courseTitle: PropTypes.string,
    email: PropTypes.string,
    endDate: PropTypes.string,
    error: PropTypes.bool,
    executionMode: PropTypes.string,
    exercises: PropTypes.array,
    firstName: PropTypes.string,
    geogebraExercises: PropTypes.array,
    getAssignmentById: PropTypes.func,
    getGoogleAssignmentEndDate: PropTypes.func,
    getCorrectionByStatusId: PropTypes.func,
    getCorrectionBySubmissionId: PropTypes.func,
    getGoogleStudentInfo: PropTypes.func,
    googleCourseWorkId: PropTypes.string,
    googleUserInfo: PropTypes.object,
    googleUserStatus: PropTypes.shape({
        error: PropTypes.bool,
        pending: PropTypes.bool,
        success: PropTypes.bool,
    }),
    heartbeat: PropTypes.func,
    heartbeatPulse: PropTypes.bool,
    homeworkOriginalId: PropTypes.string,
    homeworkVersion: PropTypes.number,
    httpStatusCodeAuth: PropTypes.number,
    httpStatusCodeStatus: PropTypes.number,
    idCat: PropTypes.number,
    initializeApplication: PropTypes.func,
    isApplicationEmbedded: PropTypes.bool,
    isAuthLoaded: PropTypes.bool,
    isAuthLoading: PropTypes.bool,
    isLtiLaunch: PropTypes.bool,
    isOldCorrection: PropTypes.bool,
    isReady: PropTypes.bool,
    userAllowed: PropTypes.bool,
    lastName: PropTypes.string,
    loginId: PropTypes.string,
    modificationTime: PropTypes.string,
    notLoggedModalShowed: PropTypes.bool,
    onPlayVideo: PropTypes.func,
    pauseHomework: PropTypes.func,
    publicTitle: PropTypes.string,
    remainingTime: PropTypes.number,
    resumeLogin: PropTypes.func,
    resumeGoogleLogin: PropTypes.func,
    resumeGoogleAddOnLogin: PropTypes.func,
    saveStatus: PropTypes.bool,
    score: PropTypes.number,
    scoreModifier: PropTypes.object,
    originalScore: PropTypes.number,
    setHomework: PropTypes.func,
    showNotLoggedModal: PropTypes.func,
    singleAnswer: PropTypes.bool,
    source: PropTypes.string,
    status: PropTypes.object,
    subjects: PropTypes.array,
    swapCorrectionMode: PropTypes.func,
    terminateHomework: PropTypes.func,
    time: PropTypes.number,
    videos: PropTypes.array,
};

const mapStateToProps = (state) => {
    return {
        answers: state.status.answers,
        assignment: state.assignment,
        assignmentId: state.status.assignmentId,
        backSource: !!state.application.googleCourseWorkId ? SOURCES.GOOGLE_CLASSROOM : state.application.source,
        bookId: state.application.bookId,
        calculateVoteRequest: state.status.calculateVoteRequest,
        callbackLabel: state.application.app,
        callbackUrl: state.application.url,
        correctionPoints: state.status.correctionPoints,
        correctionsStatus: state.status.correctionsStatus,
        courseTitle: state.homework.courseTitle,
        email: state.status.student.email,
        endDate: state.assignment.endDate,
        error:
            state.application.status.error ||
            state.homework.status.error ||
            state.status.httpStatus.error ||
            state.auth.status.error ||
            state.assignment.httpStatus.error,
        executionMode: state.application.executionMode,
        exercises: state.homework.exercises,
        firstName: state.status.student.firstName,
        geogebraExercises: state.homework.geogebraExercises,
        googleCourseWorkId: state.application.googleCourseWorkId,
        googleUserInfo: state.googleAuth.userInfo,
        googleUserStatus: state.googleAuth.status,
        heartbeatPulse: state.heartbeat.pulse,
        homeworkOriginalId: state.homework.id,
        homeworkVersion: state.status.homeworkVersion,
        httpStatusCodeAuth: state.auth.status.httpStatus,
        httpStatusCodeStatus: state.status.httpStatus.httpCode,
        idCat: state.homework.idCat,
        isApplicationEmbedded: state.application.isEmbedded,
        isLtiLaunch: state.application.isLtiLaunch,
        isOldCorrection: state.status.isOldCorrection,
        isAuthLoaded:
            (!state.auth.status.fetching && (state.auth.status.loaded || state.auth.status.error)) ||
            (!state.googleAuth.status.pending && (state.googleAuth.success || state.googleAuth.status.error)),
        isAuthLoading: state.auth.status.fetching || state.googleAuth.status.pending,
        isReady:
            state.homework.status.loaded &&
            state.status.httpStatus.loaded &&
            state.auth.status.loaded &&
            (state.application.googleCourseWorkId ? state.status.student.isGoogle && state.status.student.loaded : true) &&
            (state.status.assignmentId ? state.assignment.httpStatus.loaded : true),
        userAllowed: [SCHOOL_TEACHER_GROUP_ID, UNIVERSITY_TEACHER_GROUP_ID, FUNCTIONARY_GROUP_ID].indexOf(state.auth.groupId) > -1,
        lastName: state.status.student.lastName,
        modificationTime: state.status.modificationTime,
        notLoggedModalShowed: state.modal.notLoggedModalShowed,
        publicTitle: state.assignment?.title ?? state.homework.publicTitle,
        remainingTime: state.status.remainingTime,
        score: state.status.score,
        scoreModifier: state.status.scoreModifier,
        originalScore: state.status.originalScore,
        singleAnswer: state.application.singleAnswer,
        source: state.application.source,
        status: state.status,
        subjects: state.homework.subjects,
        time: state.status.profileValues.time ?? state.application.time,
        videos: state.mediaPlayer.videos,
    };
};

const actionCreators = {
    calculateVoteTraining,
    correctAllExercises,
    getAssignmentById,
    getGoogleAssignmentEndDate,
    getCorrectionByStatusId,
    getCorrectionBySubmissionId,
    getGoogleStudentInfo,
    initializeApplication,
    onPlayVideo,
    pauseHomework: pauseHomeworkAction,
    resumeLogin,
    resumeGoogleLogin,
    resumeGoogleAddOnLogin,
    setHomework,
    showNotLoggedModal: showNotLoggedModalAction,
    swapCorrectionMode,
    terminateHomework,
};

export default connect(mapStateToProps, actionCreators)(CorrectionPage);
